import types from '../mutation-types';
import ContactAPI from '../../api/contacts';
import {
  createLabelRequestGate,
  normalizeLabelTitles,
  updatingLabelTitles,
} from 'dashboard/helper/labelRequestGate';

const state = {
  records: {},
  pending: {},
  uiFlags: {
    isFetching: false,
    isUpdating: false,
    isError: false,
  },
};

const labelUpdateGate = createLabelRequestGate();

const hasOwn = (collection, id) =>
  Object.prototype.hasOwnProperty.call(collection, Number(id));

export const getters = {
  getUIFlags($state) {
    return $state.uiFlags;
  },
  getContactLabels: $state => id => {
    return $state.records[Number(id)] || [];
  },
  getPendingLabels: $state => id => {
    const numericId = Number(id);
    if (!hasOwn($state.pending, numericId)) return null;
    return $state.pending[numericId];
  },
  getUpdatingLabels: $state => id => {
    const numericId = Number(id);
    return updatingLabelTitles(
      $state.records[numericId],
      hasOwn($state.pending, numericId) ? $state.pending[numericId] : null
    );
  },
};

export const actions = {
  get: async ({ commit }, contactId) => {
    commit(types.SET_CONTACT_LABELS_UI_FLAG, {
      isFetching: true,
    });
    try {
      const response = await ContactAPI.getContactLabels(contactId);
      commit(types.SET_CONTACT_LABELS, {
        id: contactId,
        data: response.data.payload,
      });
      commit(types.SET_CONTACT_LABELS_UI_FLAG, {
        isFetching: false,
      });
    } catch (error) {
      commit(types.SET_CONTACT_LABELS_UI_FLAG, {
        isFetching: false,
      });
    }
  },
  update: async ({ commit }, { contactId, labels }) => {
    const id = Number(contactId);
    const normalized = normalizeLabelTitles(labels);

    commit(types.SET_CONTACT_LABELS_PENDING, {
      id,
      data: normalized,
    });

    return labelUpdateGate.run(
      id,
      normalized,
      async (requestLabels, isLatest) => {
        commit(types.SET_CONTACT_LABELS_UI_FLAG, {
          isUpdating: true,
          isError: false,
        });
        try {
          const response = await ContactAPI.updateContactLabels(
            id,
            requestLabels
          );
          if (!isLatest()) return response.data?.payload;

          const payload = Array.isArray(response.data?.payload)
            ? response.data.payload
            : requestLabels;
          commit(types.SET_CONTACT_LABELS, {
            id,
            data: payload,
          });
          commit(types.SET_CONTACT_LABELS_PENDING, {
            id,
            data: null,
          });
          commit(types.SET_CONTACT_LABELS_UI_FLAG, {
            isUpdating: false,
            isError: false,
          });
          return payload;
        } catch (error) {
          if (isLatest()) {
            commit(types.SET_CONTACT_LABELS_PENDING, {
              id,
              data: null,
            });
            commit(types.SET_CONTACT_LABELS_UI_FLAG, {
              isUpdating: false,
              isError: true,
            });
          }
          throw new Error(error);
        }
      }
    );
  },
  setContactLabel({ commit }, { id, data }) {
    commit(types.SET_CONTACT_LABELS, { id, data });
  },
};

export const mutations = {
  [types.SET_CONTACT_LABELS_UI_FLAG]($state, data) {
    $state.uiFlags = {
      ...$state.uiFlags,
      ...data,
    };
  },
  [types.SET_CONTACT_LABELS]: ($state, { id, data }) => {
    $state.records = {
      ...$state.records,
      [Number(id)]: data,
    };
  },
  [types.SET_CONTACT_LABELS_PENDING]: ($state, { id, data }) => {
    const pending = { ...$state.pending };
    if (data == null) delete pending[Number(id)];
    else pending[Number(id)] = data;
    $state.pending = pending;
  },
};

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations,
};
